import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
  getFieldNamesFromColumnConfig,
} from '~/components/SolaForceDataGrid';
import { SubstituteManagerReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common/useReportNavigateToView';
import { convertToDateOrUndefined } from '~/utils/helper';

type SubstituteManagerReportRow_Manager = {
  manager_employeeId: number;
  manager_firstName: string;
  manager_lastName: string;
  manager_employeeNumber: string;
  manager_organizationUnit: string;
  manager_organizationUnitNumber: string;
};

type SubstituteManagerReportRow_SubstituteManager = {
  substituteManager_employeeId: number;
  substituteManager_firstName: string;
  substituteManager_lastName: string;
  substituteManager_employeeNumber: string;
  substituteManager_organizationUnit: string;
  substituteManager_organizationUnitNumber: string;
  startDate?: Date;
  endDate?: Date;
};

type SubstituteManagerReportRow = {
  rowId: string;
} & SubstituteManagerReportRow_Manager &
  SubstituteManagerReportRow_SubstituteManager;

type Props = {
  data: SubstituteManagerReportData[];
};

const convertSubstituteManagerReportDataToRows = (props: {
  data: SubstituteManagerReportData[];
}): SubstituteManagerReportRow[] => {
  const { data } = props;

  return data.flatMap((entry, index): SubstituteManagerReportRow[] => {
    const {
      fOrgUnitName,
      fOrgUnitNumber,
      fOrgUnitCurMgrNumber,
      fOrgUnitCurMgrFirstName,
      fOrgUnitCurMgrLastName,
      fOrgUnitCurMgrId,
    } = entry;

    return entry.fOrgUnitSubstitute.map((sub, subIndex) => ({
      rowId: `${fOrgUnitCurMgrId}-${sub.fEmpId}-${index}-${subIndex}`,
      manager_employeeId: fOrgUnitCurMgrId,
      manager_firstName: fOrgUnitCurMgrFirstName,
      manager_lastName: fOrgUnitCurMgrLastName,
      manager_employeeNumber: fOrgUnitCurMgrNumber,
      manager_organizationUnit: fOrgUnitName,
      manager_organizationUnitNumber: fOrgUnitNumber,
      substituteManager_employeeId: sub.fEmpId,
      substituteManager_firstName: sub.fEmpFirstName,
      substituteManager_lastName: sub.fEmpLastName,
      substituteManager_employeeNumber: sub.fEmpNumber,
      substituteManager_organizationUnit: sub.fEmpUnitName,
      substituteManager_organizationUnitNumber: sub.fEmpUnitCode.toString(),
      startDate: convertToDateOrUndefined(sub.fSubEmpStartDate),
      endDate: convertToDateOrUndefined(sub.fSubEmpEndDate),
    }));
  });
};

export const useSubstituteManagerReport = ({ data }: Props) => {
  const { t } = useTranslation();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const { rows } = useMemo(() => {
    return {
      rows: convertSubstituteManagerReportDataToRows({ data }),
    };
  }, [data]);

  const { columnConfigs, columnGroupingModel } = useMemo(() => {
    const managerColumnConfigs: GridColDef<SubstituteManagerReportRow_Manager>[] =
      [
        {
          field: 'manager_employeeId',
          headerName: t('laPicture'),
          filterable: false,
          sortable: false,
          disableExport: true,
          renderCell: (params) => {
            return (
              <EmployeeCellComponent
                {...params}
                pictureOnly={true}
                onClick={() => {
                  goToEmployeeBasicDataView(params.row.manager_employeeId);
                }}
              />
            );
          },
          minWidth: 60,
        },
        {
          field: 'manager_firstName',
          headerName: t('laFirstName'),
          hideable: false,
          renderCell: (params) => {
            return (
              <ButtonCellComponent
                {...params}
                onClick={() => {
                  goToEmployeeBasicDataView(params.row.manager_employeeId);
                }}
              />
            );
          },
          minWidth: 90,
        },
        {
          field: 'manager_lastName',
          headerName: t('laLastName'),
          hideable: false,
          renderCell: (params) => {
            return (
              <ButtonCellComponent
                {...params}
                onClick={() => {
                  goToEmployeeBasicDataView(params.row.manager_employeeId);
                }}
              />
            );
          },
          minWidth: 110,
        },
        {
          field: 'manager_employeeNumber',
          headerName: t('laEmpNumber'),
          minWidth: 130,
        },
        {
          field: 'manager_organizationUnit',
          headerName: t('laOrgUnit'),
          minWidth: 130,
        },
        {
          field: 'manager_organizationUnitNumber',
          headerName: t('laUnitNumber'),
          minWidth: 130,
        },
      ];

    const substituteManagerColumnConfigs: GridColDef<SubstituteManagerReportRow_SubstituteManager>[] =
      [
        {
          field: 'substituteManager_employeeId',
          headerName: t('laPicture'),
          filterable: false,
          sortable: false,
          disableExport: true,
          renderCell: (params) => {
            return (
              <EmployeeCellComponent
                {...params}
                pictureOnly={true}
                onClick={() => {
                  goToEmployeeBasicDataView(
                    params.row.substituteManager_employeeId,
                  );
                }}
              />
            );
          },
          minWidth: 60,
        },
        {
          field: 'substituteManager_firstName',
          headerName: t('laFirstName'),
          renderCell: (params) => {
            return (
              <ButtonCellComponent
                {...params}
                onClick={() => {
                  goToEmployeeBasicDataView(
                    params.row.substituteManager_employeeId,
                  );
                }}
              />
            );
          },
          minWidth: 90,
        },
        {
          field: 'substituteManager_lastName',
          headerName: t('laLastName'),
          renderCell: (params) => {
            return (
              <ButtonCellComponent
                {...params}
                onClick={() => {
                  goToEmployeeBasicDataView(
                    params.row.substituteManager_employeeId,
                  );
                }}
              />
            );
          },
          minWidth: 110,
        },
        {
          field: 'substituteManager_employeeNumber',
          headerName: t('laEmpNumber'),
          minWidth: 130,
        },
        {
          field: 'substituteManager_organizationUnit',
          headerName: t('laOrgUnit'),
          minWidth: 130,
        },
        {
          field: 'substituteManager_organizationUnitNumber',
          headerName: t('laUnitNumber'),
          minWidth: 130,
        },
        {
          field: 'startDate',
          type: 'date',
          headerName: t('laStartDate'),
          valueFormatter: dateCellValueFormatter,
        },
        {
          field: 'endDate',
          type: 'date',
          headerName: t('laEndDate'),
          valueFormatter: dateCellValueFormatter,
        },
      ];

    const columnConfigs = [
      ...managerColumnConfigs,
      ...substituteManagerColumnConfigs,
    ] as GridColDef<SubstituteManagerReportRow>[];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'manager',
        headerName: t('laManager'),
        children: getFieldNamesFromColumnConfig(managerColumnConfigs),
      },
      {
        groupId: 'substituteManager',
        headerName: t('fOrgUnitSubstituteManager'),
        children: getFieldNamesFromColumnConfig(substituteManagerColumnConfigs),
      },
    ];
    return {
      columnConfigs,
      columnGroupingModel,
    };
  }, [t, goToEmployeeBasicDataView]);

  return {
    table: {
      columnConfigs,
      columnGroupingModel,
      rows,
    },
  };
};
